import type { LucideIcon } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '../../lib/cn';
import { Tooltip } from './tooltip';

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: LucideIcon;
  label: string;
  size?: 'sm' | 'md' | 'lg';
  active?: boolean;
  /** Masque l'info-bulle (ex. bouton déjà accompagné d'un texte). */
  noTooltip?: boolean;
}

const SIZES = {
  sm: { button: 'size-8', icon: 18 },
  md: { button: 'size-10', icon: 20 },
  lg: { button: 'size-12', icon: 24 },
} as const;

/** Bouton rond à icône, avec info-bulle et libellé accessible. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon: Icon, label, size = 'md', active, noTooltip, className, ...props },
  ref,
) {
  const button = (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full text-muted transition-colors',
        'hover:bg-hover hover:text-fg focus-visible:bg-hover-strong focus-visible:outline-none',
        'disabled:pointer-events-none disabled:opacity-40',
        active && 'text-fg',
        SIZES[size].button,
        className,
      )}
      {...props}
    >
      <Icon size={SIZES[size].icon} strokeWidth={active ? 2.4 : 2} aria-hidden />
    </button>
  );
  return noTooltip ? button : <Tooltip label={label}>{button}</Tooltip>;
});
